/* eslint-disable i18next/no-literal-string -- .*/
import {
  NetworkRole,
  useUpdateUserNetworkRoleMutation,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { FormField } from '@/components/base/form/form-field';
import { Input } from '@/components/base/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/components/base/form/select';
import { PageHeading } from '@/components/base/page-heading';
import Section from '@/components/base/section';
import { cn } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { enqueueSnackbar } from 'notistack';
import { type FormEvent } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const items = [
  { id: 1, value: NetworkRole.Affiliate, label: 'Affiliate' },
  { id: 2, value: NetworkRole.Mlm, label: 'MLM' },
];

const updateSchema = z.object({
  userNumber: z.string(),
  networkRole: z.nativeEnum(NetworkRole),
});
type Update = z.infer<typeof updateSchema>;

export function UpdateNetworkRole(): JSX.Element {
  const [updateUserNetworkRole, { loading }] =
    useUpdateUserNetworkRoleMutation();

  const methods = useForm<Update>({
    resolver: zodResolver(updateSchema),
    mode: 'onChange',
  });
  const { handleSubmit, getValues, setValue, watch } = methods;

  async function onSubmit(): Promise<void> {
    const { userNumber, networkRole } = getValues();
    try {
      await updateUserNetworkRole({
        variables: {
          userNumber: Number(userNumber),
          networkRole,
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar({
          message: e.message,
          variant: 'error',
        });
      }
    }
  }

  function handleClickSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    return void (async () => {
      handleSubmit(onSubmit)();
    })();
  }

  return (
    <Section variant="secondary" className="max-w-3xl">
      <div className="flex w-full flex-col">
        <PageHeading>Update User Network Role</PageHeading>

        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              handleClickSubmit(e);
            }}
            className="flex flex-col gap-5"
          >
            <div className="flex w-full flex-col gap-5 sm:flex-row">
              <fieldset className="w-full">
                <FormField label="User Number" name="userNumber">
                  <Input
                    variant="secondary"
                    type="number"
                    placeholder="User Number"
                  />
                </FormField>
              </fieldset>

              <fieldset className="w-full">
                <Select
                  onValueChange={(value: NetworkRole) => {
                    setValue('networkRole', value);
                  }}
                >
                  <span className="mb-1 text-sm font-normal text-default-gray-400">
                    Network Role
                  </span>
                  <SelectTrigger
                    className="h-full w-full gap-5 whitespace-nowrap"
                    size="sm"
                    variant="secondary"
                  >
                    <div className="flex w-full items-center gap-2">
                      <span
                        className={cn('text-sm', {
                          'text-default-gray-400': !watch('networkRole'),
                        })}
                      >
                        {watch('networkRole') || 'Select Network Role'}
                      </span>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup className="flex max-h-40 flex-col gap-2 overflow-y-auto">
                      {items.map((role) => (
                        <SelectItem key={role.id} value={role.value}>
                          <span className="max-w-[300px] truncate md:max-w-none">
                            {role.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </fieldset>
            </div>

            <div className="flex w-full justify-end">
              <Button loading={loading} type="submit">
                Increase
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Section>
  );
}
